import { useRef, useState, useCallback } from "react";
import type { CellMouseDownEvent, CellMouseOverEvent } from "ag-grid-community";

export interface CellRange {
  startRow: number;
  endRow: number;
  startCol: string;
  endCol: string;
}

export interface CellRangePosition {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}

export function useCellRangeSelection() {
  const [range, setRange] = useState<CellRange | null>(null);
  const rangeRef = useRef<CellRange | null>(null);
  const isDragging = useRef(false);

  const updateRange = useCallback((next: CellRange | null) => {
    rangeRef.current = next;
    setRange(next);
  }, []);

  const onCellMouseDown = useCallback((e: CellMouseDownEvent) => {
    // 좌클릭만 허용 (우클릭은 컨텍스트 메뉴)
    if ((e.event as MouseEvent)?.button !== 0) return;
    if (e.rowIndex == null) return;
    if (e.rowPinned) return; // 합계행 제외

    isDragging.current = true;
    updateRange({
      startRow: e.rowIndex,
      endRow: e.rowIndex,
      startCol: e.column.getColId(),
      endCol: e.column.getColId(),
    });

    window.addEventListener(
      "mouseup",
      () => {
        isDragging.current = false;
      },
      { once: true },
    );
  }, [updateRange]);

  const onCellMouseOver = useCallback((e: CellMouseOverEvent) => {
    if (!isDragging.current) return;
    if (e.rowIndex == null) return;
    if (e.rowPinned) return; // 합계행 제외

    const prev = rangeRef.current;
    if (!prev) return;

    const endRow = e.rowIndex;
    const endCol = e.column.getColId();
    if (prev.endRow === endRow && prev.endCol === endCol) return; // 불필요한 갱신 방지

    updateRange({ ...prev, endRow, endCol });
  }, [updateRange]);

  /**
   * ref 기반 조회 - 참조가 영구 고정되므로 colDef에 넣어도 stale closure가 발생하지 않음.
   * 시각 갱신은 range(state) 변경 → refreshCells로 별도 트리거.
   */
  const getCellRangePosition = useCallback(
    (rowIndex: number, colId: string, colIds: string[]): CellRangePosition | null => {
      const r = rangeRef.current;
      if (!r) return null;

      const cStart = colIds.indexOf(r.startCol);
      const cEnd = colIds.indexOf(r.endCol);
      const cIdx = colIds.indexOf(colId);
      if (cStart < 0 || cEnd < 0 || cIdx < 0) return null;

      const rMin = Math.min(r.startRow, r.endRow);
      const rMax = Math.max(r.startRow, r.endRow);
      const cMin = Math.min(cStart, cEnd);
      const cMax = Math.max(cStart, cEnd);

      if (rowIndex < rMin || rowIndex > rMax || cIdx < cMin || cIdx > cMax) return null;

      return {
        top: rowIndex === rMin,
        bottom: rowIndex === rMax,
        left: cIdx === cMin,
        right: cIdx === cMax,
      };
    },
    [],
  );

  const isCellInRange = useCallback(
    (rowIndex: number, colId: string, colIds: string[]) =>
      getCellRangePosition(rowIndex, colId, colIds) !== null,
    [getCellRangePosition],
  );

  const clearRange = useCallback(() => updateRange(null), [updateRange]);

  const selectAll = useCallback((lastRowIndex: number, firstColId: string, lastColId: string) => {
    if (lastRowIndex < 0) return;
    updateRange({
      startRow: 0,
      endRow: lastRowIndex,
      startCol: firstColId,
      endCol: lastColId,
    });
  }, [updateRange]);

  return {
    range,
    onCellMouseDown,
    onCellMouseOver,
    getCellRangePosition,
    isCellInRange,
    clearRange,
    selectAll,
  };
}
